import React, { useState } from 'react';
import { Waves, Shield, Award, Calendar, Newspaper, Activity, CreditCard, Settings, Menu, X, PhoneCall, User as UserIcon, LogOut, LayoutDashboard } from 'lucide-react';
import { User } from '../types';

interface HeaderProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  user: User | null;
  noticeBar: string;
  onOpenAuth: () => void;
  onOpenUserCenter: () => void;
  onLogout: () => void;
}

export const Header: React.FC<HeaderProps> = ({ activeTab, setActiveTab, user, noticeBar, onOpenAuth, onOpenUserCenter, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const navItems = [
    { id: 'home', label: '首页', icon: Waves },
    { id: 'events', label: '游泳赛事', icon: Calendar },
    { id: 'news', label: '会员动态', icon: Newspaper },
    { id: 'safety', label: '防溺水宣传', icon: Shield },
    { id: 'certificates', label: '证书中心', icon: Award },
    { id: 'stats', label: '训练看板', icon: Activity },
    { id: 'payments', label: '微信缴费', icon: CreditCard },
  ];

  const handleNavClick = (id: string) => {
    setActiveTab(id);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0 });
  };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
      {/* Top Banner Notice */}
      <div className="bg-gradient-to-r from-sky-800 via-blue-900 to-indigo-900 text-white text-xs py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="bg-sky-500/30 text-sky-200 text-[10px] font-semibold px-2 py-0.5 rounded border border-sky-400/30">官方门户</span>
            <span className="truncate">{noticeBar}</span>
          </div>
          <div className="flex items-center gap-4 text-sky-200 text-[11px] shrink-0">
            <span className="flex items-center gap-1">
              <PhoneCall className="w-3 h-3 text-sky-400" />
              紧急救援热线: 0854-6821999
            </span>
          </div>
        </div>
      </div>

      {/* Main Header Nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div onClick={() => handleNavClick('home')} className="flex items-center gap-3 cursor-pointer group">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-sky-600 via-blue-600 to-cyan-500 flex items-center justify-center text-white shadow-lg shadow-sky-500/20 group-hover:scale-105 transition-transform duration-300">
              <Waves className="w-7 h-7" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-slate-900 tracking-tight group-hover:text-sky-600 transition-colors">长顺县游泳协会</h1>
                <span className="text-[10px] font-bold text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded border border-sky-200">CSXSWIM</span>
              </div>
              <p className="text-xs text-slate-500 hidden sm:block">Changshun County Swimming Association</p>
            </div>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button key={item.id} onClick={() => handleNavClick(item.id)}
                  className={`relative flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${isActive ? 'bg-sky-50 text-sky-700 font-semibold shadow-sm' : 'text-slate-600 hover:text-sky-600 hover:bg-slate-50'}`}>
                  <Icon className={`w-4 h-4 ${isActive ? 'text-sky-600' : 'text-slate-400'}`} />
                  <span>{item.label}</span>
                  {isActive && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-sky-600 rounded-full" />}
                </button>
              );
            })}
          </nav>

          {/* Right Side: user / admin */}
          <div className="hidden sm:flex items-center gap-3 relative">
            <a href="/admin.php" title="后台管理" className="w-9 h-9 rounded-xl border border-slate-200 text-slate-500 hover:text-sky-600 hover:border-sky-300 flex items-center justify-center transition-colors">
              <Settings className="w-4.5 h-4.5 w-5 h-5" />
            </a>
            {user ? (
              <div className="relative">
                <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 pl-1 pr-3 py-1.5 rounded-xl border border-sky-200 bg-sky-50 hover:bg-sky-100 transition-colors">
                  <span className="w-7 h-7 rounded-full bg-gradient-to-tr from-sky-600 to-blue-600 text-white text-xs font-bold flex items-center justify-center">
                    {user.name.slice(0, 1)}
                  </span>
                  <span className="text-xs font-bold text-sky-800 max-w-[70px] truncate">{user.name}</span>
                  {user.role === 'member' && <span className="text-[9px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full font-bold">会员</span>}
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-2xl border border-slate-100 p-2 z-50">
                    <div className="px-3 py-2 border-b border-slate-100 mb-1">
                      <div className="text-sm font-bold text-slate-900">{user.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{user.phone}</div>
                      {user.member_id && <div className="text-[11px] text-emerald-600 font-bold mt-1">会员编号 {user.member_id}</div>}
                    </div>
                    <button onClick={() => { setUserMenuOpen(false); onOpenUserCenter(); }} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50">
                      <UserIcon className="w-4 h-4 text-sky-600" /> 个人中心
                    </button>
                    <button onClick={() => { setUserMenuOpen(false); onOpenUserCenter(); setActiveTab('payments'); }} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50">
                      <CreditCard className="w-4 h-4 text-sky-600" /> 我的订单
                    </button>
                    {user.role === 'admin' && (
                      <a href="/admin.php" className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50">
                        <LayoutDashboard className="w-4 h-4 text-sky-600" /> 后台管理
                      </a>
                    )}
                    <button onClick={() => { setUserMenuOpen(false); onLogout(); }} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold text-red-600 hover:bg-red-50">
                      <LogOut className="w-4 h-4" /> 退出登录
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button onClick={onOpenAuth}
                className="px-4 py-2 bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-500 hover:to-blue-500 text-white text-xs font-semibold rounded-xl shadow-md shadow-sky-600/20 transition-all hover:shadow-lg hover:-translate-y-0.5">
                登录 / 注册
              </button>
            )}
          </div>

          {/* Mobile hamburger */}
          <div className="lg:hidden flex items-center gap-2">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2.5 rounded-xl text-slate-600 hover:text-sky-600 hover:bg-slate-100 focus:outline-none" aria-label="菜单">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 pt-3 pb-6 space-y-2 shadow-xl">
          <div className="grid grid-cols-2 gap-2 mb-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button key={item.id} onClick={() => handleNavClick(item.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium text-left transition-colors ${isActive ? 'bg-sky-50 text-sky-700 font-bold border border-sky-200' : 'bg-slate-50 text-slate-700 hover:bg-slate-100'}`}>
                  <Icon className={`w-4 h-4 ${isActive ? 'text-sky-600' : 'text-slate-500'}`} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
          <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
            {user ? (
              <button onClick={() => { setMobileMenuOpen(false); onOpenUserCenter(); }} className="w-full py-2.5 bg-sky-600 text-white text-xs font-bold rounded-xl text-center shadow">
                个人中心（{user.name}）
              </button>
            ) : (
              <button onClick={() => { setMobileMenuOpen(false); onOpenAuth(); }} className="w-full py-2.5 bg-sky-600 text-white text-xs font-bold rounded-xl text-center shadow">
                登录 / 注册
              </button>
            )}
            <a href="/admin.php" className="w-full py-2.5 bg-slate-100 text-slate-700 text-xs font-bold rounded-xl text-center">后台管理</a>
          </div>
        </div>
      )}
    </header>
  );
};
